// @ts-nocheck

import { isMatch } from "lodash-es";

/**
 * Type referring to all the keys inside a dictionary, using dot notation
 */
export type DotNotationKeys<T> = T extends object
  ? {
      [K in keyof T]: T[K] extends any[]
        ? `${K}`
        : `${K}` | `${K}.${DotNotationKeys<T[K]>}`;
    }[keyof T]
  : never;

/**
 * Get the nested type of a key inside a dictionary, using dot notation
 */
export type NestedType<
  T,
  K extends DotNotationKeys<T>,
> = K extends `${infer FirstKey}.${infer RestKeys}`
  ? FirstKey extends keyof T
    ? NestedType<T[FirstKey], RestKeys>
    : never
  : K extends keyof T
    ? T[K]
    : never;

/**
 * Get a property from an object, given a dot notation field, with type checking
 * for typescript using genericity
 * @param obj any object
 * @param key a valid string key using dot notation
 * @returns the value of the given key inside the dictionary
 */
export function genericGetProperty<T, K extends DotNotationKeys<T>>(
  obj: T,
  key: K,
): NestedType<T, K> {
  return key.split(".").reduce((o: T, k: string) => o?.[k], obj) as NestedType<
    T,
    K
  >;
}

/**
 * Set a property in an object, given a dot notation field, with type checking
 * for typescript using genericity
 * @param obj any object
 * @param key a valid string key using dot notation
 * @param value new value for the given key
 */
export function genericSetProperty<T, K extends DotNotationKeys<T>>(
  obj: T,
  key: K,
  value: NestedType<T, K>,
): void {
  let currentObj: T = obj;
  const paths = key.split(".");
  const finalKey = paths.pop();
  let currentPath = "";

  for (const p of paths) {
    currentPath = currentPath ? `${currentPath}.${p}` : p;
    if (typeof currentObj[p] !== "object" || currentObj[p] === undefined) {
      throw new Error(`Property '${currentPath}' does not exist`);
    }
    // nosemgrep: javascript.lang.security.audit.prototype-pollution.prototype-pollution-loop.prototype-pollution-loop
    currentObj = currentObj[p];
  }
  currentObj[finalKey] = value;
}

/**
 * A utility type that makes all properties of a given type optional,
 * including nested properties. This type recursively applies `Partial`
 * to every property in the object, allowing for deep partial updates.
 *
 * @template T - The object type to apply deep partiality to.
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Check whether any of the contents of `partial` variable are not
 * deep equal to those of `fullItem`. This function uses a recursive approach
 * to compare nested objects.
 *
 * @param fullItem - The original item to be compared against.
 * @param partial - The partial data to be compared.
 * @returns `true` if any part of the `partial` object is not deeply equal to the corresponding part of `fullItem`, otherwise `false`.
 */
export function deepPartialChanged<T extends object>(
  fullItem: T,
  partial: DeepPartial<T>,
) {
  return !isMatch(fullItem, partial);
}
